/**
 * Reusable Table Component
 * Matches AngularJS design patterns with clean HTML table structure
 */

import React from 'react';

export interface TableColumn {
  key: string;
  title: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render?: (value: any, record: any, index: number) => React.ReactNode;
  onClick?: (record: any) => void;
}

export interface TableProps {
  columns: TableColumn[];
  data: any[];
  className?: string;
  striped?: boolean;
  bordered?: boolean;
  hover?: boolean;
  emptyText?: string;
  loading?: boolean;
  onRowClick?: (record: any) => void;
}

const Table: React.FC<TableProps> = ({
  columns,
  data,
  className = '',
  striped = true,
  bordered = true,
  hover = true,
  emptyText = 'No data available',
  loading = false,
  onRowClick
}) => {
  const getTableClasses = () => {
    let classes = 'tec-table';
    if (striped) classes += ' tec-table-striped';
    if (bordered) classes += ' tec-table-bordered';
    if (hover) classes += ' tec-table-hover';
    if (onRowClick) classes += ' tec-table-clickable';
    if (className) classes += ` ${className}`;
    return classes;
  };

  const getCellValue = (record: any, column: TableColumn) => {
    if (column.render) {
      return column.render(record[column.key], record, 0);
    }
    return record[column.key] || '';
  };

  const getCellAlign = (column: TableColumn) => {
    return column.align || 'left';
  };

  if (loading) {
    return (
      <div className="tec-table-container">
        <div className="tec-table-empty">
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="tec-table-container">
      <div className="tec-table-responsive">
        <table className={getTableClasses()} style={{ tableLayout: 'fixed', width: '100%' }}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  style={{
                    width: column.width,
                    textAlign: getCellAlign(column)
                  }}
                >
                  {column.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="tec-table-empty">
                  {emptyText}
                </td>
              </tr>
            ) : (
              data.map((record, index) => (
                <tr
                  key={record.id || index}
                  onClick={onRowClick ? () => onRowClick(record) : undefined}
                  style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                >
                  {columns.map((column) => {
                    // Column-specific click handler takes priority
                    const cellClickHandler = column.onClick
                      ? (e: React.MouseEvent) => {
                          e.stopPropagation();
                          column.onClick!(record);
                        }
                      : undefined;

                    return (
                      <td
                        key={column.key}
                        style={{
                          textAlign: getCellAlign(column),
                          cursor: column.onClick ? 'pointer' : 'inherit',
                          overflowWrap: 'break-word',
                          wordBreak: 'normal'
                        }}
                        onClick={cellClickHandler}
                      >
                        {getCellValue(record, column)}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Table;